import { css } from '@emotion/core'
import { Alert, Cell, Checkbox, Grid, HFlow, InfoLabel, Modal, ModalBody, ModalFooter, VFlow } from 'bold-ui'
import { FooterButton } from 'components/footer-button'
import { CheckboxField, DateField, Form, FormRenderProps, TextAreaField } from 'components/form'
import {
  EstrategiaVacinacaoSelectField,
  EstrategiaVacinacaoSelectFieldModel,
} from 'components/form/field/select/EstrategiaVacinacaoSelectField'
import { GrupoAtendimentoSelectField } from 'components/form/field/select/GrupoAtendimentoSelectField'
import { LocalAplicacaoSelectField } from 'components/form/field/select/LocalAplicacaoSelectField'
import { LoteImunobiologicoSelectField } from 'components/form/field/select/LoteImunobiologicoSelectField/LoteImunobiologicoSelectField'
import { ViaAdministracaoVacinaSelectField } from 'components/form/field/select/ViaAdministracaoVacinaSelectField'
import { addDays } from 'date-fns'
import createCalculator from 'final-form-calculate'
import { EstrategiaVacinacaoEnum } from 'graphql/types.generated'
import React from 'react'
import { FormSpy } from 'react-final-form'
import { metaPath } from 'util/metaPath'
import { createValidator, required } from 'util/validation'

import { RegistroVacinacaoFormModel, TipoRegistroDoseEnum } from './model'
import { VacinacaoModalHeader } from './VacinacaoModalHeader'

interface AplicacaoVacinaModalProps {
  idImunobiologico: ID
  nomeImunobiologico: string
  siglaImunobiologico: string
  idDose: ID
  nomeDose: string
  faixaEtaria: string
  registroAnterior?: boolean
  modalOpen: boolean
  handleModalClose(): void
  handleOnSubmit(formValues: RegistroVacinacaoFormModel): void
}

const meta = metaPath<RegistroVacinacaoFormModel>()

const validator = createValidator<RegistroVacinacaoFormModel>({
  estrategiaVacinacao: [required],
})

const calculator = createCalculator(
  {
    field: meta.viaAdministracao.absolutePath(),
    updates: {
      [meta.localAplicacaoVacinacao.absolutePath()]: () => {
        return null
      },
    },
  },
  {
    field: meta.estrategiaVacinacao.absolutePath(),
    updates: {
      [meta.grupoAtendimento.absolutePath()]: (
        value: EstrategiaVacinacaoSelectFieldModel,
        values: RegistroVacinacaoFormModel
      ) => {
        return value?.estrategiaVacinacaoDbEnum !== EstrategiaVacinacaoEnum.CAMPANHA ? null : values.grupoAtendimento
      },
    },
  }
)

const minDate = addDays(new Date(), 1)

export const AplicacaoVacinaModal = (props: AplicacaoVacinaModalProps) => {
  const { idImunobiologico, nomeImunobiologico, idDose, nomeDose, modalOpen, handleModalClose, handleOnSubmit } = props

  const handleSubmit = (formValues: RegistroVacinacaoFormModel) => {
    formValues.tipoRegistroVacinacao = TipoRegistroDoseEnum.APLICACAO
    formValues.imunobiologico = idImunobiologico
    formValues.doseImunobiologico = idDose
    formValues.dataAplicacao = new Date().toISOString()
    formValues.dataRegistro = new Date().toISOString()
    handleOnSubmit(formValues)
    handleModalClose()
  }

  const renderForm = (formRenderProps: FormRenderProps<RegistroVacinacaoFormModel>) => {
    return (
      <Modal open={modalOpen} onClose={handleModalClose} size='auto' closeOnBackdropClick={false}>
        <ModalBody>
          <VFlow>
            <VacinacaoModalHeader {...props} />
            <Grid
              style={css`
                width: 45rem;
              `}
            >
              <Cell size={12}>
                <Alert type='info' inline>
                  É recomendado aplicar a {nomeDose} do imunobiológico {nomeImunobiologico} somente a partir da 20ª
                  semana de gestação.
                </Alert>
              </Cell>
              <Cell size={12}>
                <CheckboxField
                  label='Registro anterior'
                  name={meta.isRegistroAnterior}
                  onChange={function noRefCheck() {}}
                />
              </Cell>
              <Cell size={6}>
                <EstrategiaVacinacaoSelectField
                  name={meta.estrategiaVacinacao}
                  label='Estratégia'
                  imunobiologicoId={idImunobiologico}
                  doseImunobiologicoId={idDose}
                  required
                />
              </Cell>
              <Cell size={6}>
                <FormSpy subscription={{ values: true }}>
                  {({ values }) =>
                    values.estrategiaVacinacao?.estrategiaVacinacaoDbEnum === EstrategiaVacinacaoEnum.CAMPANHA ? (
                      <GrupoAtendimentoSelectField name={meta.grupoAtendimento} label='Grupo de atendimento' required />
                    ) : null
                  }
                </FormSpy>
              </Cell>
              <Cell size={4}>
                <FormSpy subscription={{ values: true }}>
                  {({ values }) => (
                    <DateField
                      label='Aprazamento da próxima dose'
                      name={meta.dataAprazamento}
                      icon='calendarOutline'
                      minDate={minDate}
                      disabled={!values.estrategiaVacinacao}
                    />
                  )}
                </FormSpy>
              </Cell>
              <Cell size={2}>
                <InfoLabel title='REV aprazada para'>-</InfoLabel>
              </Cell>
              <Cell size={6} />
              <Cell size={6}>
                <LoteImunobiologicoSelectField
                  name={meta.loteImunobiologico}
                  label='Lote/Fabricante'
                  imunobiologicoId={idImunobiologico}
                  required
                />
              </Cell>
              <Cell size={6} alignSelf='flex-end'>
                <Checkbox label='Cadastrar novo lote' onChange={function noRefChec() {}} />
              </Cell>
              <Cell size={6}>
                <ViaAdministracaoVacinaSelectField name={meta.viaAdministracao} label='Via de Administração' required />
              </Cell>
              <Cell size={6}>
                <FormSpy subscription={{ values: true }}>
                  {({ values }) => (
                    <LocalAplicacaoSelectField
                      name={meta.localAplicacaoVacinacao}
                      label='Local da aplicação'
                      viaAdministracaoId={values?.viaAdministracao?.id}
                      required
                      disabled={!values.viaAdministracao}
                    />
                  )}
                </FormSpy>
              </Cell>
              <Cell size={12}>
                <TextAreaField
                  style={css`
                    height: 5rem;
                    resize: none;
                  `}
                  name={meta.observacoes}
                  label='Observações'
                  maxLength={200}
                />
              </Cell>
            </Grid>
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <FooterButton kind='normal' onClick={handleModalClose}>
              Cancelar
            </FooterButton>
            <FooterButton kind='primary' onClick={formRenderProps.handleSubmit}>
              Salvar
            </FooterButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }
  return (
    <Form<RegistroVacinacaoFormModel>
      subscription={{ submitting: true, pristine: true }}
      render={renderForm}
      decorators={[calculator]}
      validate={validator}
      onSubmit={handleSubmit}
    />
  )
}
